import React, { useEffect, useState } from "react";
import { format } from "date-fns";

import {
    Button,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
} from "@formant/ui-kit";
import { Calendar } from "@formant/ui-kit/src/components/ui/calendar";

interface IDateTimePickerProps {
    value?: Date;
    onChange?: (date: Date | undefined) => void;
}

export const DateTimePicker: React.FC<IDateTimePickerProps> = ({
    value,
    onChange,
}) => {
    const [date, setDate] = useState<Date | undefined>(value);
    const [hours, setHours] = useState<string>("12");
    const [minutes, setMinutes] = useState<string>("00");
    const [period, setPeriod] = useState<string>("PM");
    const [useUtc, setUseUtc] = useState(false);
    const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>(
        value,
    );

    // Update the combined date and time when any component changes
    useEffect(() => {
        if (date) {
            const newDate = new Date(date);
            const isPM = period === "PM";
            let hourValue = parseInt(hours, 10);

            // Convert to 24-hour format for the Date object
            if (isPM && hourValue < 12) {
                hourValue += 12;
            } else if (!isPM && hourValue === 12) {
                hourValue = 0;
            }

            if (useUtc) {
                // Convert local time to UTC
                const localDate = new Date(newDate);
                localDate.setHours(hourValue, parseInt(minutes, 10));
                const utcDate = new Date(
                    localDate.getTime() - localDate.getTimezoneOffset() * 60000,
                );
                setSelectedDateTime(utcDate);
                onChange?.(utcDate);
            } else {
                newDate.setHours(hourValue, parseInt(minutes, 10));
                setSelectedDateTime(newDate);
                onChange?.(newDate);
            }
        }
    }, [date, hours, minutes, period, useUtc, onChange]);

    // Generate hours options (1-12)
    const hoursOptions = Array.from({ length: 12 }, (_, i) => {
        const hour = i + 1;
        return hour.toString().padStart(2, "0");
    });

    // Generate minutes options (00-59)
    const minutesOptions = Array.from({ length: 60 }, (_, i) => {
        return i.toString().padStart(2, "0");
    });

    return (
        <Popover>
            <PopoverTrigger asChild={true}>
                <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-formant-color-background-secondary border-formant-color-border"
                >
                    <div className="flex items-center">
                        {selectedDateTime ? (
                            <>
                                {format(selectedDateTime, "PPP")}
                                <span className="mx-2">at</span>
                                {format(selectedDateTime, "h:mm a")}
                                {useUtc && " UTC"}
                            </>
                        ) : (
                            <span>Pick a date and time</span>
                        )}
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[15.625rem] !min-w-[15.625rem] p-0 bg-black border border-[var(--color-border-active)]"
                align="start"
            >
                <div className="flex flex-col">
                    <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus={true}
                        className="w-full"
                        disabled={currentDate => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return currentDate < today;
                        }}
                        defaultMonth={new Date()}
                    />
                    <div className="p-4 border-t border-formant-color-border">
                        <div className="grid gap-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="flex flex-col space-y-2">
                                    <Label>Hours</Label>
                                    <Select
                                        value={hours}
                                        onValueChange={setHours}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Hour" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {hoursOptions.map(hour => (
                                                <SelectItem
                                                    key={hour}
                                                    value={hour}
                                                >
                                                    {hour}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col space-y-2">
                                    <Label>Minutes</Label>
                                    <Select
                                        value={minutes}
                                        onValueChange={setMinutes}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Minute" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {minutesOptions.map(minute => (
                                                <SelectItem
                                                    key={minute}
                                                    value={minute}
                                                >
                                                    {minute}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col space-y-2">
                                    <Label>AM/PM</Label>
                                    <Select
                                        value={period}
                                        onValueChange={setPeriod}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="AM/PM" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="AM">
                                                AM
                                            </SelectItem>
                                            <SelectItem value="PM">
                                                PM
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2 pt-2">
                                <Switch
                                    id="use-utc"
                                    checked={useUtc}
                                    onCheckedChange={setUseUtc}
                                />
                                <Label htmlFor="use-utc">Use UTC Time</Label>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
